/**
 * The phone card list is another presentation of the desktop table model
 * (useReportTable): the same sorted tree, labels and formatted cells, plus
 * each node's line count for the card's sub-line. Pure, for direct tests.
 */
import type React from "react";
import { TreeTableNode } from "../../../common/GroupedTreeTable";
import { ReportMetricDef, ReportTreeNodeV2 } from "../../../../types/reportsV2";

export interface ReportCardNode {
  /** The table node's stable id (keeps a card open across re-runs). */
  id: string;
  /** Date bucket ("Tue 10/6/26") or group value ("Ana Lee"). */
  label: string;
  lineCount: number;
  /** Formatted values by metric key (as in the table cells). */
  cells: Record<string, React.ReactNode>;
  children: ReportCardNode[];
}

/**
 * Card nodes from the table model: `rows` are the table's formatted nodes,
 * `source` the sorted run tree they were built from (same order and shape).
 */
export function toCardNodes(
  source: ReportTreeNodeV2[],
  rows: TreeTableNode[],
): ReportCardNode[] {
  return rows.map((row, i) => ({
    id: row.id,
    label: row.title ?? String(row.label),
    lineCount: source[i]?.line_count ?? 0,
    cells: row.cells,
    children: toCardNodes(source[i]?.children ?? [], row.children ?? []),
  }));
}

/** Every card id, depth first. */
export function collectCardIds(cards: ReportCardNode[]): string[] {
  const out: string[] = [];
  const walk = (list: ReportCardNode[]) =>
    list.forEach((card) => {
      out.push(card.id);
      walk(card.children);
    });
  walk(cards);
  return out;
}

/**
 * The metric a closed card shows beside its title: the first summary total
 * that is also a visible column, else the first column.
 */
export function primaryMetric(
  columns: ReportMetricDef[],
  kpis: string[],
): ReportMetricDef | null {
  const key = kpis.find((k) => columns.some((c) => c.key === k));
  return columns.find((c) => c.key === key) ?? columns[0] ?? null;
}

export interface VisibleCard {
  node: ReportCardNode;
  /** 0 for date buckets, then one per group level (indent). */
  depth: number;
}

/**
 * The cards on screen, depth first: every top-level card, and the children
 * of each open card. Paging counts these (as the desktop table pages its
 * visible rows), so Expand all can't render an unbounded list.
 */
export function flattenVisibleCards(
  cards: ReportCardNode[],
  isOpen: (id: string) => boolean,
): VisibleCard[] {
  const out: VisibleCard[] = [];
  const walk = (list: ReportCardNode[], depth: number) =>
    list.forEach((node) => {
      out.push({ node, depth });
      if (isOpen(node.id)) walk(node.children, depth + 1);
    });
  walk(cards, 0);
  return out;
}
